/**
 * 集計グラフ用のポインタ追従ツールチップ (docs/27 §9-3、2026-09-16 オーナー指示)。
 *
 * 棒の一部にマウスを乗せた (スマホでは触れた) ときに、その部分の内訳をポインタのそばに出す。
 * `title` 属性では表示までに時間がかかり、スマホでは出ないため、自前で描く。
 * ページ全体に被せるので `createPortal` で body 直下に出し、画面外へはみ出さないよう位置を丸める。
 */
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ChartTooltipLine {
  label: string;
  value: string;
  /** 左に置く色見本 (省略可)。 */
  color?: string;
}

interface TooltipState {
  x: number;
  y: number;
  title: string;
  lines: ChartTooltipLine[];
}

const OFFSET = 14;
const WIDTH = 220;

export function useChartTooltip() {
  const [state, setState] = useState<TooltipState | null>(null);

  const show = useCallback(
    (
      e: { clientX: number; clientY: number } | React.TouchEvent,
      title: string,
      lines: ChartTooltipLine[],
    ) => {
      const point =
        'touches' in e && e.touches.length > 0
          ? { clientX: e.touches[0]!.clientX, clientY: e.touches[0]!.clientY }
          : (e as { clientX: number; clientY: number });
      setState({ x: point.clientX, y: point.clientY, title, lines });
    },
    [],
  );

  const hide = useCallback(() => setState(null), []);

  const node =
    state && typeof document !== 'undefined'
      ? createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-50 rounded border border-slate-200 bg-white px-2 py-1.5 text-xs shadow-lg"
            style={{
              // 画面右端・下端でははみ出さないよう反対側へ寄せる。
              left: Math.min(state.x + OFFSET, Math.max(8, window.innerWidth - WIDTH - 8)),
              top: Math.min(state.y + OFFSET, Math.max(8, window.innerHeight - 120)),
              width: `${WIDTH}px`,
            }}
          >
            <p className="mb-0.5 font-semibold text-slate-800">{state.title}</p>
            {state.lines.map((line) => (
              <p key={line.label} className="flex items-center gap-1 text-slate-600">
                {line.color && (
                  <span
                    aria-hidden="true"
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm border border-slate-300"
                    style={{ background: line.color }}
                  />
                )}
                <span className="min-w-0 flex-1 truncate">{line.label}</span>
                <span className="shrink-0 font-semibold tabular-nums text-slate-800">
                  {line.value}
                </span>
              </p>
            ))}
          </div>,
          document.body,
        )
      : null;

  return { show, hide, node };
}
